import { FC, useEffect, useMemo, useState } from "react"
import { useAppSelector } from "../../../app/hooks"
import {
  selectWbReports,
  WbReportState,
} from "../../../app/reducers/reportsSlice"
import { STATUS } from "../../../utils/const"
import "./FinReport.css"
import { getMessage } from "../../../msgs/msgs"
import BaseApi from "../../../api/api"
import { formWbReportXlsx } from "./formXlsx"
import { Tip, TipType } from "../../Tip/Tip"
import { Button, Heading, Text, TextField } from "@radix-ui/themes"
import { Select } from "../../Select/Select"
import { DataTable, type DataTableColumn } from "../../DataTable/DataTable"

interface FinReportProps {}

const getStatusClass = (status: STATUS) => {
  switch (status) {
    case STATUS.SUCCESS:
      return "status-success"
    case STATUS.ERROR:
      return "status-error"
    case STATUS.IN_PROGRESS:
      return "status-pending"
    default:
      return "status-unknown"
  }
}

export const FinReport: FC<FinReportProps> = () => {
  const reports = useAppSelector(selectWbReports)
  const [selectedDate, setSelectedDate] = useState<string>("")
  const [periodFrom, setPeriodFrom] = useState<string>("")
  const [periodTo, setPeriodTo] = useState<string>("")
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(false)

  const handleDownloadReport = async (report: WbReportState) => {
    const { dateFrom } = report
    const response = await BaseApi.getReport(dateFrom)
    const reportData = response?.data?.report
    reportData && formWbReportXlsx(`wb-daily-${dateFrom}`, reportData)
  }

  const { minDate, maxDate } = useMemo(() => {
    const dates = reports
      .filter((r) => r.isDaily && r.status === STATUS.SUCCESS)
      .map((r) => r.dateFrom)
      .sort()
    return { minDate: dates[0] ?? "", maxDate: dates[dates.length - 1] ?? "" }
  }, [reports])

  useEffect(() => {
    if (periodTo && periodFrom && periodTo < periodFrom) {
      setPeriodTo("")
    }
  }, [periodTo, periodFrom])

  const handleDownloadPeriodReport = async () => {
    if (!periodFrom || !periodTo) return
    setIsLoadingPeriod(true)
    try {
      const response = await BaseApi.getReportRange(periodFrom, periodTo)
      const reportData = response?.data?.report
      if (reportData?.length) {
        formWbReportXlsx(`wb-period-${periodFrom}-${periodTo}`, reportData)
      }
    } finally {
      setIsLoadingPeriod(false)
    }
  }

  const reportByMonthMap = useMemo(() => {
    return reports.reduce((acc, report) => {
      const date = report.dateFrom
      const d = new Date(date)
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const year = d.getFullYear()
      const key = `${month}-${year}`

      if (!acc.has(key)) acc.set(key, [])
      acc.get(key)?.push(report)

      return acc
    }, new Map<string, WbReportState[]>())
  }, [reports])

  const dailyReports =
    reportByMonthMap
      .get(selectedDate)
      ?.filter((report) => report.isDaily)
      .sort(
        (a, b) =>
          new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime(),
      ) ?? []

  const columns: DataTableColumn<WbReportState>[] = [
    {
      key: "dateFrom",
      header: "Дата",
      cell: (report) =>
        new Date(report.dateFrom)?.toLocaleDateString() || report.dateFrom,
    },
    {
      key: "status",
      header: "Статус",
      cell: (report) => (
        <span className={`status-badge ${getStatusClass(report.status)}`}>
          {(report.status === STATUS.ERROR && report.errors?.message) ||
            getMessage(report.status)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Действия",
      cell: (report) => (
        <div className="action-buttons">
          <Button
            onClick={() => handleDownloadReport(report)}
            disabled={report.status !== STATUS.SUCCESS}
            className="action-button download-button"
          >
            Скачать
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="fin-report w100">
      <Tip
        type={TipType.WARNING}
        content={
          "Внимание! Закупочные цены актуальны только для товаров, цены которых обновлялись через текущий сайт."
        }
      />
      <div className="reports-section">
        <Heading mb="2" size="4">
          Отчет за период
        </Heading>
        <div className="period-controls">
          <label className="period-label">
            <Text size="2" color="gray">
              С
            </Text>
            <TextField.Root
              type="date"
              size="3"
              value={periodFrom}
              min={minDate}
              max={periodTo || maxDate}
              onChange={(e) => setPeriodFrom(e.target.value)}
            />
          </label>
          <label className="period-label">
            <Text size="2" color="gray">
              По
            </Text>
            <TextField.Root
              type="date"
              size="3"
              value={periodTo}
              min={periodFrom || minDate}
              max={maxDate}
              onChange={(e) => setPeriodTo(e.target.value)}
            />
          </label>
          <Button
            size={"3"}
            disabled={!periodFrom || !periodTo || isLoadingPeriod}
            onClick={handleDownloadPeriodReport}
          >
            {isLoadingPeriod ? "Загрузка..." : "Скачать за период"}
          </Button>
        </div>
      </div>
      <div className="reports-section">
        <Heading mb="2" size="4">
          Ежедневные отчеты
        </Heading>
        <Select
          size="3"
          value={selectedDate}
          onChange={setSelectedDate}
          options={Array.from(reportByMonthMap.keys()).map((key) => ({
            value: key,
            label: formatMonthKey(key),
          }))}
          placeholder="Выберите дату"
        />
        <div className="reports-table-container">
          <DataTable
            columns={columns}
            data={dailyReports}
            rowKey={(report, i) => i}
          />
        </div>
      </div>
    </div>
  )
}

function formatMonthKey(key: string, locale = "ru-RU"): string {
  const [monthStr, yearStr] = key.split("-") // "02-2024" -> ["02", "2024"]
  const month = Number(monthStr) - 1 // Date: 0–11
  const year = Number(yearStr)

  const date = new Date(year, month, 1)

  // Месяц прописью + год, напр. "февраль 2024 г."
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(date)
}
